import { HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, Observable, of, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ApplicationConfig } from '../../../core/config/application.config';
import { Page } from '../../../shared/model/local/pagination/page';
import { PageCriteria } from '../../../shared/model/local/pagination/page-criteria';
import { HttpUtil } from '../../../shared/util/http.util';
import { ObjectUtil } from '../../../shared/util/object.util';
import {
  MedicalCaseProcessedContent,
  MedicalCaseProcessedDocument,
  MedicalCaseDocumentUploadPhase,
} from '../model';
import { MedicalCase } from '../model/medical-case';
import { MedicalCaseCreateDto } from '../model/medical-case-create.dto';
import { MedicalCaseUpdateDto } from '../model/medical-case-update.dto';
import { MedicalCaseDocument } from '../model/medical-case-document';
import { MedicalCasePageItem } from '../model/medical-case-page-item';
import {
  MedicalCasePhase,
  MedicalCasePhaseDocument,
  MedicalCasePhases,
} from '../model/medical-case-phase';
import { MedicalCaseProcessingRequestDto } from '../model/medical-case-processing-request.dto';
import { MedicalCaseDocumentVersionMetadataUpdateDto } from '../model/medical-case-document-version-metadata-update.dto';

export interface MedicalCaseVersionIdsPayload {
  medicalCaseDocumentVersionIds: string[];
}

type MedicalCaseDocumentVersionState = 'APPROVED' | 'DISAPPROVED';

interface MedicalCaseDocumentVersionStateRequest {
  medicalCaseDocumentVersionIds: string[];
  state: MedicalCaseDocumentVersionState;
}

@Injectable({ providedIn: 'root' })
export class MedicalCaseApiService {
  private readonly http = inject(HttpUtil);
  private readonly baseUrl = ApplicationConfig.apiUrl;
  private readonly updatedCases: Record<string, MedicalCase> = {};

  getPage(criteria: PageCriteria): Observable<Page<MedicalCasePageItem>> {
    const plain = ObjectUtil.instanceToPlain(criteria) as Record<
      string,
      unknown
    >;
    const params: Record<string, unknown> = {};

    params['pageNumber'] = plain['pageNumber'] ?? 0;
    params['pageSize'] = plain['pageSize'] ?? 10;

    if (
      plain['sortOrders'] !== null &&
      plain['sortOrders'] !== undefined &&
      plain['sortOrders'] !== ''
    ) {
      params['sortOrders'] = plain['sortOrders'];
    }
    if (
      plain['searchPhrase'] !== null &&
      plain['searchPhrase'] !== undefined &&
      plain['searchPhrase'] !== ''
    ) {
      params['searchPhrase'] = plain['searchPhrase'];
    }

    const archivedEq = plain['archivedEq'];
    params['archivedEq'] = archivedEq === true;

    return (this.http.get(`${this.baseUrl}/medical-cases`, params) as Observable<
      Page<MedicalCasePageItem>
    >).pipe(
      map(page => ({
        ...page,
        content: (page.content ?? []).map(item => {
          const updated = this.updatedCases[item.id];
          if (updated == null) {
            return item;
          }

          return {
            ...item,
            name: updated.name,
            patientLanguage: updated.patientLanguage,
          };
        }),
      }))
    );
  }

  getById(id: string): Observable<MedicalCase> {
    return (
      this.http.get(
        `${this.baseUrl}/medical-cases/${id}`
      ) as Observable<MedicalCase>
    ).pipe(
      map(medicalCase => this.applyUpdatedCase(this.normalizeMedicalCaseDocuments(medicalCase)))
    );
  }

  getByIdSilent(id: string): Observable<MedicalCase> {
    return (
      this.http.get(
        `${this.baseUrl}/medical-cases/${id}`,
        {},
        {
          silentMode: true,
        }
      ) as Observable<MedicalCase>
    ).pipe(
      map(medicalCase => this.applyUpdatedCase(this.normalizeMedicalCaseDocuments(medicalCase)))
    );
  }

  getProcessingCaseSnapshot(caseId: string): Observable<MedicalCase> {
    return this.getByIdSilent(caseId);
  }

  create(dto: MedicalCaseCreateDto): Observable<MedicalCase> {
    return this.http.post(`${this.baseUrl}/medical-cases`, dto, {
      silentMode: true,
    }) as Observable<MedicalCase>;
  }

  update(id: string, dto: MedicalCaseUpdateDto): Observable<MedicalCase> {
    return this.getByIdSilent(id).pipe(
      map(medicalCase => {
        const updatedMedicalCase: MedicalCase = {
          ...medicalCase,
          name: dto.name,
          questions: dto.questions,
          description: dto.description,
          patientLanguage: dto.patientLanguage,
        };

        this.updatedCases[id] = updatedMedicalCase;

        return updatedMedicalCase;
      })
    );
  }

  archive(id: string): Observable<void> {
    return this.http.delete(`${this.baseUrl}/medical-cases/${id}`, {
      silentMode: true,
    }) as Observable<void>;
  }

  uploadDocument$(
    caseId: string,
    file: File,
    phase: MedicalCaseDocumentUploadPhase = 'MEDICAL_RECORD_INIT'
  ): Observable<MedicalCaseDocument | number> {
    const formData = new FormData();
    formData.append('documentFile', file, file.name);
    formData.append('request', new Blob([JSON.stringify({ phase })], { type: 'application/json' }));
    return this.http
      .upload(
        `${this.baseUrl}/medical-cases/${caseId}/medical-case-documents`,
        formData,
        { silentMode: true }
      )
      .pipe(
        map((event: HttpEvent<MedicalCaseDocument>) => {
          if (event.type === HttpEventType.Sent) {
            return 0;
          }
          if (event.type === HttpEventType.UploadProgress && event.total) {
            return Math.round((100 * event.loaded) / event.total);
          }
          if (event.type === HttpEventType.Response && event.body) {
            return event.body as MedicalCaseDocument;
          }
          return -1;
        }),
        filter(v => v !== -1)
      );
  }

  archiveDocument(caseId: string, documentId: string): Observable<void> {
    return this.http.delete(
      `${this.baseUrl}/medical-cases/${caseId}/medical-case-documents/${documentId}`,
      { ignoreError: true, silentMode: true }
    ) as Observable<void>;
  }

  updateDocumentVersionMetadata$(
    caseId: string,
    documentId: string,
    versionId: string,
    dto: MedicalCaseDocumentVersionMetadataUpdateDto,
    silentMode = false
  ): Observable<void> {
    return this.http.patch(
      `${this.baseUrl}/medical-cases/${caseId}/medical-case-documents/${documentId}/versions/${versionId}`,
      dto,
      { silentMode }
    ) as Observable<void>;
  }

  updateDocumentFile(
    caseId: string,
    documentId: string,
    file: File,
    sourceVersionId?: string | null,
    silentMode = false
  ): Observable<MedicalCaseDocument> {
    const formData = new FormData();
    formData.append('documentFile', file, file.name);
    const params: Record<string, unknown> = {};
    if (sourceVersionId != null) {
      params['sourceVersionId'] = sourceVersionId;
    }
    return this.http.post(
      `${this.baseUrl}/medical-cases/${caseId}/medical-case-documents/${documentId}/versions`,
      formData,
      { silentMode },
      params
    ) as Observable<MedicalCaseDocument>;
  }

  approveVersions$(
    caseId: string,
    dto: MedicalCaseVersionIdsPayload
  ): Observable<void> {
    return this.setDocumentVersionsState$(caseId, {
      medicalCaseDocumentVersionIds: dto.medicalCaseDocumentVersionIds,
      state: 'APPROVED',
    });
  }

  disapproveVersions$(
    caseId: string,
    dto: MedicalCaseVersionIdsPayload
  ): Observable<void> {
    return this.setDocumentVersionsState$(caseId, {
      medicalCaseDocumentVersionIds: dto.medicalCaseDocumentVersionIds,
      state: 'DISAPPROVED',
    });
  }

  startProcessing(
    caseId: string,
    dto: MedicalCaseProcessingRequestDto
  ): Observable<void> {
    return this.http.post(
      `${this.baseUrl}/medical-cases/${caseId}/processing`,
      dto,
      { silentMode: true }
    ) as Observable<void>;
  }

  getProcessingResult(
    caseId: string,
    fileId: string
  ): Observable<MedicalCaseProcessedContent> {
    return this.getFileContent(caseId, fileId).pipe(
      switchMap(content => from(content.text())),
      map(textContent => this.parseProcessingResult(textContent))
    );
  }

  getFileContent(caseId: string, fileId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/medical-cases/${caseId}/files/${fileId}`,
      {},
      { silentMode: true, responseType: 'blob' }
    );
  }

  private setDocumentVersionsState$(
    caseId: string,
    dto: MedicalCaseDocumentVersionStateRequest
  ): Observable<void> {
    return this.http.post(
      `${this.baseUrl}/medical-cases/${caseId}/medical-case-documents/versions/state`,
      dto,
      { silentMode: true }
    ) as Observable<void>;
  }

  private parseProcessingResult(
    textContent: string
  ): MedicalCaseProcessedContent {
    try {
      const parsed = JSON.parse(textContent);
      if (Array.isArray(parsed)) {
        return parsed as MedicalCaseProcessedDocument[];
      }
      return textContent;
    } catch {
      return textContent;
    }
  }

  private normalizeMedicalCaseDocuments(medicalCase: MedicalCase): MedicalCase {
    const normalizedDocuments =
      this.mapInitPhaseDocumentsToMedicalCaseDocuments(medicalCase.phases);
    const normalizedPhases = this.normalizeMedicalCasePhases(medicalCase.phases);
    if (normalizedDocuments.length === 0) {
      return {
        ...medicalCase,
        phases: normalizedPhases,
        documents: medicalCase.documents ?? [],
      };
    }

    return {
      ...medicalCase,
      phases: normalizedPhases,
      documents: normalizedDocuments,
    };
  }

  private applyUpdatedCase(medicalCase: MedicalCase): MedicalCase {
    const updated = this.updatedCases[medicalCase.id];

    if (updated == null) {
      return medicalCase;
    }

    return {
      ...medicalCase,
      name: updated.name,
      questions: updated.questions,
      description: updated.description,
      patientLanguage: updated.patientLanguage,
    };
  }

  private normalizeMedicalCasePhases(
    phases: MedicalCasePhases | null | undefined
  ): MedicalCasePhases | undefined {
    if (phases == null) {
      return phases ?? undefined;
    }

    return { ...phases };
  }

  private mapInitPhaseDocumentsToMedicalCaseDocuments(
    phases: MedicalCasePhases | null | undefined
  ): MedicalCaseDocument[] {
    const initPhase = phases?.MEDICAL_RECORD_INIT ?? phases?.SECOND_OPINION_INIT;
    const initDocuments = initPhase?.documents ?? [];

    return initDocuments
      .map(document => this.mapInitPhaseDocument(document))
      .filter((document): document is MedicalCaseDocument => document != null);
  }

  private mapInitPhaseDocument(
    document: MedicalCasePhaseDocument
  ): MedicalCaseDocument | null {
    const activeVersion = document.activeVersion;
    const originalFileId = activeVersion?.fileId?.trim() ?? '';
    if (!originalFileId) {
      return null;
    }

    const name = activeVersion?.fileName?.trim() || 'Unknown';

    return {
      id:
        document.originalDocumentId?.trim() ||
        document.originalDocument?.id?.trim() ||
        document.id,
      name,
      originalDocumentName: document.originalDocumentName?.trim() || document.originalDocument?.fileName?.trim() || name,
      originalFileId,
    };
  }
}
